<script setup lang="ts">
import SvgIcon from "~/components/Common/SvgIcon.vue";
import { useSideModals } from "~/composables/utils/useSideModals";
import { useAttendanceReviewStore } from "~/store/attendance/attendance-review.store";
import { useCopilotPanelStore } from "~/store/copilot/copilot-panel.store";
import { useSessionDetailsAttendeesStore } from "~/store/sessions/session-details-attendees-store";
import { useCreateTrainingStore } from "~/store/trainings/create-training.store";
import { useEmployeeTrainingsStore } from "~/store/employee-trainings/employee-trainings.store";

/*
 * The Simbel AI trigger, pinned to the bottom-right corner of every page: the header no longer
 * carries one, so this is the only way into the panel outside the home banner.
 */
const attendanceReviewStore = useAttendanceReviewStore();
const copilotPanelStore = useCopilotPanelStore();
const createTrainingStore = useCreateTrainingStore();
const employeeTrainingsStore = useEmployeeTrainingsStore();
const sessionDetailsAttendeesStore = useSessionDetailsAttendeesStore();
const { hasOpenSideModal } = useSideModals();
const route = useRoute();

/*
 * Five cases stand it down:
 * - a public page (`isLoggedIn: false`), such as the partner's attendee-list link or a survey — even for a
 *   logged-in admin, the assistant does not belong there;
 * - the panel is open — it takes the very corner the button rests in, and closes from its own cross;
 * - a `SideModal` is on screen. It is anchored to the same right edge and the button lands on its
 *   footer actions. A z-index cannot settle this: a side modal opened *inside* a session detail is
 *   trapped in that `GlobalModal`'s stacking context (500), so no value it carries can beat a button
 *   painted outside it — and lowering the button under 500 would lose the session detail, which is
 *   exactly where the assistant has to stay reachable;
 * - the « Ajout de participant(s) » modal is on screen, for the same reason: it fills the session detail
 *   and its footer puts the confirm button, with the tooltip saying why it is disabled, in this corner;
 * - the attendance review or the training form's exit confirm is on screen. `CopilotShell` drops
 *   *under* those (`above-modals`), so a press here would open the panel behind them: a dead button.
 *   Every other modal, a session detail and the training form included, keeps it.
 */
const isStoodDown = computed(
  () =>
    route.meta.isLoggedIn === false ||
    copilotPanelStore.isOpen ||
    hasOpenSideModal.value ||
    sessionDetailsAttendeesStore.showAddAttendeeModal ||
    attendanceReviewStore.isOpen ||
    createTrainingStore.showExit,
);

/*
 * Below `sm`, a participant's session detail pins its actions, stacked full width, to the bottom edge. How many
 * apply varies, so no fixed lift clears them: the button steps aside there, through the bar's own breakpoint.
 * The hosts render that detail on this same condition.
 */
const isOverSessionActionBar = computed(() => employeeTrainingsStore.showMySessionModal && !!route.query.id?.length);
</script>

<template>
  <!--
    Over the whole 500 modal layer — a session detail is a full-screen `GlobalModal` and the
    assistant has to stay one press away from there too; still under the panel itself (600) and the
    header overlays (700).

    The corner clears whatever owns the bottom of the screen, through the very breakpoint that puts
    it there rather than through a measured viewport, so nothing shifts after the first paint:
    - above 768px, an `IndexLayout`'s pagination. It is in the page flow and ends ~63px off the
      bottom edge (`page_container` `pb-3` 12 + the row's ~39 + `Pagination`'s `pt-3` 12), and its
      right half — « éléments par page » — is in this very corner, so 64px is the floor: lower, and
      the button lands on it;
    - below it, `NavBarMobile` — 70px tall, `fixed bottom-0`, and it covers that same pagination.

    The surface is the panel's own — white, bordered, blue `ai` glyph — rather than a solid accent
    disc: it is the same object. The page ground it floats over is white, so the border takes the
    darker `stroke-grey-color` and the lift comes from `--shadow-floating`, two layers cast downwards
    rather than the flat glow the panel uses.

    No fixed width: `px-3` around the 32px glyph *is* the 56px disc, so the bubble is free to
    grow to whatever the label needs. It grows leftwards on its own — the corner is pinned by
    `right-6` — and the glyph leads, so it travels left ahead of the letters typing in behind it.
  -->
  <button
    v-if="!isStoodDown"
    type="button"
    class="group fixed bottom-[86px] right-6 z-[550] flex h-14 cursor-pointer items-center justify-center rounded-full border border-stroke-grey-color bg-light-color px-3 shadow-[shadow:var(--shadow-floating)] transition-colors hover:bg-lightest-grey-color min-[769px]:bottom-[64px]"
    :class="{ 'max-sm:hidden': isOverSessionActionBar }"
    data-testid="copilot-floating-trigger"
    aria-label="Ouvrir Simbel AI"
    @click="copilotPanelStore.toggle()"
  >
    <SvgIcon name="ai" class="pointer-events-none h-8 w-8 shrink-0 text-primary-blue-color" />
    <!--
      Decorative: the button carries the accessible name. `0fr → 1fr` opens the label to its own width; the clip
      and mask sit on the grid because mid-transition the item only gets f² of that width and would lag the bubble.
    -->
    <span
      class="small_demi ms-0 grid grid-cols-[0fr] overflow-hidden whitespace-nowrap text-primary-color [mask-image:linear-gradient(to_right,black_calc(100%_-_2em),transparent)] [mask-repeat:no-repeat] [mask-size:100%_100%] transition-[grid-template-columns,margin-inline-start,mask-size] duration-[120ms] ease-[ease-out] group-hover:ms-2 group-hover:grid-cols-[1fr] group-hover:[mask-size:calc(100%_+_2em)_100%] group-focus-visible:ms-2 group-focus-visible:grid-cols-[1fr] group-focus-visible:[mask-size:calc(100%_+_2em)_100%] motion-safe:duration-[400ms]"
      aria-hidden="true"
    >
      <span class="min-w-0">Demander à l'IA</span>
    </span>
  </button>
</template>